import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import ProductCard from './ProductCard';
import { FaHeart as EmptyHeart, FaHeart as FilledHeart, FaArrowLeft, FaWhatsapp, FaFilter, FaTimes, FaPhone, FaStar, FaRegStar, FaStarHalfAlt, FaShoppingCart, FaComments, FaShare, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faWhatsapp, faFacebookMessenger } from '@fortawesome/free-brands-svg-icons';
import slugify from 'slugify';
import { v4 as uuidv4 } from 'uuid';
import ReactPixel from 'react-facebook-pixel';
import ReactGA from "react-ga4";
import ReactMarkdown from 'react-markdown';
import { Helmet } from 'react-helmet';

/* ─────────────────────────────────────────────
   Fresh Food Color Palette
   Russian Red:     #CD0302  → price, danger, out-of-stock, discount badges
   Sunburst:        #F0C400  → stars/rating
   Edam:            #F5D19D  → borders, warm light surfaces
   Citrusy Orange:  #F88702  → active tabs, CTA accents, lens border
   Autumn Green:    #4F7E17  → in-stock, primary brand, add to cart
   Warm light bg:   #FFF8EC
───────────────────────────────────────────── */

// Inject Półtawski Nowy font
const FontInjector = () => {
  useEffect(() => {
    const link = document.createElement('link');
    link.rel = 'stylesheet';
    link.href = 'https://fonts.googleapis.com/css2?family=Poly:ital@0;1&family=Raleway:wght@300;400;500;600;700;800&display=swap';
    document.head.appendChild(link);

    // Inject Półtawski Nowy via @font-face (Google Fonts alternative for organic feel)
    const style = document.createElement('style');
    style.textContent = `
      @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600;0,700;1,300;1,400;1,500&family=Jost:wght@300;400;500;600;700;800&display=swap');

      :root {
        --red: #CD0302;
        --sunburst: #F0C400;
        --edam: #F5D19D;
        --orange: #F88702;
        --green: #4F7E17;
        --bg-warm: #FFF8EC;
        --bg-leaf: #F2F7EC;
        --font-display: 'Cormorant Garamond', Georgia, serif;
        --font-body: 'Jost', 'Segoe UI', sans-serif;
      }

      .pdp-root {
        font-family: var(--font-body) !important;
        min-height: 100vh;
      }

      .pdp-root * {
        font-family: var(--font-body);
      }

      .pdp-display {
        font-family: var(--font-display) !important;
      }

      /* Leaf texture background pattern */
      .leaf-pattern {
        background-image: none;
      }

      /* Organic blob shape for image container */
      .organic-card {
        border-radius: 24px 8px 24px 8px;
        transition: box-shadow 0.3s ease;
      }

      .organic-card:hover {
        box-shadow: 0 12px 40px rgba(79,126,23,0.18) !important;
      }

      /* Badge styles */
      .badge-red {
        background: var(--red);
        color: #fff;
        font-family: var(--font-body);
        font-weight: 700;
        font-size: 13px;
        padding: 4px 12px;
        border-radius: 20px 4px 20px 4px;
        letter-spacing: 0.02em;
      }

      .badge-orange {
        background: var(--orange);
        color: #fff;
        font-family: var(--font-body);
        font-weight: 700;
        font-size: 13px;
        padding: 4px 12px;
        border-radius: 20px 4px 20px 4px;
        letter-spacing: 0.02em;
      }

      /* Organic divider */
      .organic-divider {
        height: 2px;
        background: linear-gradient(to right, transparent, var(--edam), var(--orange), var(--edam), transparent);
        border: none;
        margin: 16px 0;
      }

      /* Tab active style */
      .tab-active {
        color: var(--orange) !important;
        border-bottom: 3px solid var(--orange) !important;
        font-weight: 700;
      }

      /* Btn primary - Buy Now */
      .btn-buy-now {
        background: linear-gradient(135deg, #F88702 0%, #e07a00 100%);
        color: #fff;
        font-family: var(--font-body);
        font-weight: 700;
        font-size: 16px;
        letter-spacing: 0.04em;
        border-radius: 12px 4px 12px 4px;
        padding: 14px 20px;
        border: none;
        cursor: pointer;
        transition: all 0.25s ease;
        box-shadow: 0 4px 16px rgba(248,135,2,0.35);
        width: 100%;
        text-transform: uppercase;
      }

      .btn-buy-now:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(248,135,2,0.45);
        background: linear-gradient(135deg, #ff9410 0%, #F88702 100%);
      }

      .btn-buy-now:disabled {
        background: #ccc;
        color: #999;
        cursor: not-allowed;
        box-shadow: none;
      }

      /* Btn secondary - Add to Cart */
      .btn-add-cart {
        background: linear-gradient(135deg, #4F7E17 0%, #3a5e11 100%);
        color: #fff;
        font-family: var(--font-body);
        font-weight: 700;
        font-size: 16px;
        letter-spacing: 0.04em;
        border-radius: 4px 12px 4px 12px;
        padding: 14px 20px;
        border: none;
        cursor: pointer;
        transition: all 0.25s ease;
        box-shadow: 0 4px 16px rgba(79,126,23,0.30);
        width: 100%;
        text-transform: uppercase;
      }

      .btn-add-cart:hover:not(:disabled) {
        transform: translateY(-2px);
        box-shadow: 0 8px 24px rgba(79,126,23,0.40);
        background: linear-gradient(135deg, #5d941c 0%, #4F7E17 100%);
      }

      .btn-add-cart:disabled {
        background: #ccc;
        color: #999;
        cursor: not-allowed;
        box-shadow: none;
      }

      /* Btn tertiary - Call */
      .btn-call {
        background: var(--bg-warm);
        color: #333;
        font-family: var(--font-body);
        font-weight: 600;
        font-size: 14px;
        border-radius: 10px;
        padding: 12px 16px;
        border: 2px solid var(--edam);
        cursor: pointer;
        transition: all 0.2s ease;
        width: 100%;
        text-align: center;
      }

      .btn-call:hover {
        border-color: var(--orange);
        background: #fff;
        color: var(--orange);
      }

      /* Btn WhatsApp */
      .btn-whatsapp {
        background: linear-gradient(135deg, #25d366 0%, #1aaa52 100%);
        color: #fff;
        font-family: var(--font-body);
        font-weight: 600;
        font-size: 14px;
        border-radius: 10px;
        padding: 12px 16px;
        border: none;
        cursor: pointer;
        transition: all 0.2s ease;
        width: 100%;
        text-align: center;
        box-shadow: 0 3px 12px rgba(37,211,102,0.28);
      }

      .btn-whatsapp:hover {
        transform: translateY(-1px);
        box-shadow: 0 6px 18px rgba(37,211,102,0.38);
      }

      /* Btn Messenger */
      .btn-messenger {
        background: linear-gradient(135deg, #0084ff 0%, #0065cc 100%);
        color: #fff;
        font-family: var(--font-body);
        font-weight: 600;
        font-size: 14px;
        border-radius: 10px;
        padding: 12px 16px;
        border: none;
        cursor: pointer;
        transition: all 0.2s ease;
        width: 100%;
        text-align: center;
        box-shadow: 0 3px 12px rgba(0,132,255,0.28);
      }

      .btn-messenger:hover {
        transform: translateY(-1px);
        box-shadow: 0 6px 18px rgba(0,132,255,0.38);
      }

      /* Price tag organic style */
      .price-tag {
        font-family: var(--font-display);
        font-size: 42px;
        font-weight: 600;
        color: var(--red);
        letter-spacing: -0.01em;
        line-height: 1;
      }

      .price-original {
        font-family: var(--font-body);
        font-size: 18px;
        color: #999;
        text-decoration: line-through;
        font-weight: 400;
      }

      .price-discount-badge {
        background: var(--red);
        color: #fff;
        font-size: 13px;
        font-weight: 700;
        padding: 3px 10px;
        border-radius: 20px;
        font-family: var(--font-body);
      }

      /* Product title */
      .product-title {
        font-family: var(--font-display);
        font-size: 32px;
        font-weight: 500;
        color: #1a1a1a;
        line-height: 1.2;
        letter-spacing: 0.01em;
      }

      /* Section headings */
      .section-heading {
        font-family: var(--font-display);
        font-size: 26px;
        font-weight: 600;
        color: #1a1a1a;
        letter-spacing: 0.01em;
      }

      /* Stock indicator */
      .stock-in {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background: rgba(79,126,23,0.08);
        color: var(--green);
        font-weight: 700;
        font-size: 14px;
        padding: 5px 14px;
        border-radius: 20px;
        border: 1.5px solid rgba(79,126,23,0.2);
        font-family: var(--font-body);
      }

      .stock-out {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background: rgba(205,3,2,0.06);
        color: var(--red);
        font-weight: 700;
        font-size: 14px;
        padding: 5px 14px;
        border-radius: 20px;
        border: 1.5px solid rgba(205,3,2,0.2);
        font-family: var(--font-body);
      }

      /* Timer box */
      .timer-box {
        background: linear-gradient(135deg, #fff5f5 0%, #fff0f0 100%);
        border: 2px solid rgba(205,3,2,0.15);
        border-radius: 14px;
        padding: 14px 18px;
        font-family: var(--font-body);
      }

      .timer-unit {
        background: #fff;
        border: 2px solid rgba(205,3,2,0.2);
        border-radius: 10px;
        padding: 6px 10px;
        font-size: 22px;
        font-weight: 800;
        color: var(--red);
        min-width: 52px;
        text-align: center;
        font-family: var(--font-body);
        box-shadow: 0 2px 8px rgba(205,3,2,0.1);
      }

      .timer-label {
        font-size: 10px;
        font-weight: 600;
        color: var(--red);
        opacity: 0.65;
        text-align: center;
        margin-top: 3px;
        text-transform: uppercase;
        letter-spacing: 0.06em;
      }

      /* Thumbnail */
      .thumb-btn {
        border-radius: 14px;
        overflow: hidden;
        transition: all 0.2s ease;
        border: 3px solid transparent;
        width: 72px;
        height: 72px;
        flex-shrink: 0;
        background: var(--bg-warm);
      }

      .thumb-btn.active {
        border-color: var(--orange);
        box-shadow: 0 3px 12px rgba(248,135,2,0.3);
      }

      .thumb-btn:not(.active) {
        border-color: var(--edam);
      }

      .thumb-btn:not(.active):hover {
        border-color: var(--orange);
        opacity: 0.9;
      }

      /* Right buy box card */
      .buy-box {
        background: #fff;
        border: 2px solid var(--edam);
        border-radius: 20px 8px 20px 8px;
        padding: 24px;
        position: sticky;
        top: 16px;
      }

      /* Info card */
      .info-card {
        background: #fff;
        border: 2px solid var(--edam);
        border-radius: 8px 24px 8px 24px;
        padding: 0;
        overflow: hidden;
      }

      /* Description preview box */
      .desc-preview {
        background: linear-gradient(135deg, var(--bg-warm) 0%, #fff 100%);
        border: 1.5px solid var(--edam);
        border-radius: 16px;
        overflow: hidden;
        font-family: var(--font-body);
      }

      /* Tab bar */
      .tab-bar {
        border-bottom: 2.5px solid var(--edam);
        display: flex;
        gap: 36px;
        font-family: var(--font-body);
      }

      .tab-btn {
        padding-bottom: 14px;
        padding-top: 4px;
        font-size: 17px;
        font-weight: 500;
        color: #888;
        border-bottom: 3px solid transparent;
        margin-bottom: -2.5px;
        cursor: pointer;
        background: none;
        border-left: none;
        border-right: none;
        border-top: none;
        transition: all 0.2s ease;
        letter-spacing: 0.01em;
        font-family: var(--font-body);
      }

      .tab-btn.active {
        color: var(--orange);
        border-bottom-color: var(--orange);
        font-weight: 700;
      }

      /* Spec table rows */
      .spec-row-even {
        background: var(--bg-warm);
      }
      .spec-row-odd {
        background: #fff;
      }
      .spec-key {
        font-family: var(--font-body);
        font-weight: 600;
        font-size: 15px;
        color: #555;
        padding: 14px 18px;
        border-bottom: 1px solid var(--edam);
        width: 35%;
      }
      .spec-val {
        font-family: var(--font-body);
        font-size: 15px;
        color: #222;
        padding: 14px 18px;
        border-bottom: 1px solid var(--edam);
      }

      /* Review card */
      .review-card {
        border: 1.5px solid var(--edam);
        border-radius: 16px;
        padding: 18px 22px;
        background: #fff;
        transition: box-shadow 0.2s ease;
        font-family: var(--font-body);
      }

      .review-card:hover {
        box-shadow: 0 6px 24px rgba(79,126,23,0.1);
      }

      /* Breadcrumb */
      .breadcrumb-bar {
        background: #fff;
        border-bottom: 1.5px solid var(--edam);
        padding: 14px 0;
        font-family: var(--font-body);
      }

      /* Color swatches */
      .color-swatch {
        display: flex;
        align-items: center;
        gap: 8px;
        padding: 8px 14px;
        border-radius: 30px;
        border: 2.5px solid var(--edam);
        cursor: pointer;
        transition: all 0.2s ease;
        font-size: 14px;
        font-weight: 500;
        background: #fff;
        font-family: var(--font-body);
      }

      .color-swatch.active {
        border-color: var(--orange);
        background: var(--bg-warm);
        box-shadow: 0 2px 10px rgba(248,135,2,0.2);
      }

      /* Size buttons */
      .size-btn {
        padding: 9px 18px;
        border-radius: 10px;
        border: 2px solid var(--edam);
        font-size: 15px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.2s ease;
        background: #fff;
        color: #333;
        font-family: var(--font-body);
      }

      .size-btn.active {
        border-color: var(--orange);
        background: var(--bg-warm);
        font-weight: 700;
        color: var(--orange);
        box-shadow: 0 2px 8px rgba(248,135,2,0.2);
      }

      /* Share button */
      .share-btn {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        width: 100%;
        padding: 10px 18px;
        background: #fff;
        border: 1.5px solid var(--edam);
        border-radius: 10px;
        font-size: 14px;
        font-weight: 500;
        color: #666;
        cursor: pointer;
        transition: all 0.2s ease;
        font-family: var(--font-body);
      }

      .share-btn:hover {
        border-color: var(--orange);
        color: var(--orange);
        background: var(--bg-warm);
      }

      /* Related section */
      .related-section {
        background: #fff;
        border: 2px solid var(--edam);
        border-radius: 20px;
        padding: 32px;
        font-family: var(--font-body);
      }

      /* Buyers pill */
      .buyers-pill {
        display: inline-flex;
        align-items: center;
        gap: 6px;
        background: rgba(240,196,0,0.12);
        color: #8a6d00;
        font-size: 13px;
        font-weight: 600;
        padding: 4px 14px;
        border-radius: 20px;
        border: 1px solid rgba(240,196,0,0.3);
        font-family: var(--font-body);
      }

      /* Mobile adjustments */
      @media (max-width: 767px) {
        .product-title { font-size: 20px !important; }
        .price-tag { font-size: 28px !important; }
        .section-heading { font-size: 18px !important; }
        .tab-btn { font-size: 13px !important; padding-bottom: 10px !important; }
        .tab-bar { gap: 16px !important; }
        .buy-box { border-radius: 0 !important; padding: 14px !important; }
        .info-card { border-radius: 0 !important; }
        .btn-buy-now { font-size: 14px !important; padding: 12px 16px !important; border-radius: 10px !important; }
        .btn-add-cart { font-size: 14px !important; padding: 12px 16px !important; border-radius: 10px !important; }
        .btn-call { font-size: 12px !important; padding: 10px 10px !important; }
        .btn-whatsapp { font-size: 12px !important; padding: 10px 10px !important; }
        .btn-messenger { font-size: 12px !important; padding: 10px 10px !important; }
        .organic-card { border-radius: 0 !important; }
        .timer-unit { font-size: 16px !important; min-width: 38px !important; padding: 4px 6px !important; }
        .spec-key, .spec-val { font-size: 12px !important; padding: 9px 10px !important; }
        .review-card { padding: 12px 14px !important; border-radius: 10px !important; }
        .related-section { padding: 16px 14px !important; border-radius: 10px !important; }
        .thumb-btn { width: 52px !important; height: 52px !important; }
        .color-swatch { padding: 6px 10px !important; font-size: 12px !important; }
        .size-btn { padding: 7px 12px !important; font-size: 13px !important; }
        .stock-in, .stock-out { font-size: 12px !important; padding: 4px 10px !important; }
        .buyers-pill { font-size: 11px !important; }
        .share-btn { font-size: 13px !important; }
      }

      @keyframes flyUpAndFade {
        0% { opacity: 1; transform: translateY(0) scale(1); }
        50% { opacity: 0.8; transform: translateY(-50px) scale(1.3); }
        100% { opacity: 0; transform: translateY(-120px) scale(0.8); }
      }

      @keyframes fadeInScale {
        from { opacity: 0; transform: scale(0.92) translateY(10px); }
        to { opacity: 1; transform: scale(1) translateY(0); }
      }

      .scrollbar-hide::-webkit-scrollbar { display: none; }
      .scrollbar-hide { -ms-overflow-style: none; scrollbar-width: none; }

      /* Organic leaf decorations via pseudo-elements */
      .organic-section-header::before {
        content: '🌿';
        margin-right: 8px;
        font-size: 0.85em;
      }
    `;
    document.head.appendChild(style);
  }, []);
  return null;
};

// Review Modal Component
const ReviewModal = ({ onClose, onSubmit, productName, userName, isLoggedIn }) => {
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [customerName, setCustomerName] = useState('');

  useEffect(() => {
    if (isLoggedIn && userName && userName !== 'Anonymous User' && userName !== 'User') {
      setCustomerName(userName);
    } else {
      setCustomerName('');
    }
  }, [userName, isLoggedIn]);

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(rating, comment, customerName);
  };

  const renderStarInput = (index) => {
    return (
      <span key={index} onClick={() => setRating(index)} style={{ cursor: 'pointer', fontSize: '32px', color: rating >= index ? '#F0C400' : '#ddd', transition: 'transform 0.15s', display: 'inline-block' }}
        onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.2)'}
        onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}
      >
        {rating >= index ? <FaStar /> : <FaRegStar />}
      </span>
    );
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.65)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000, backdropFilter: 'blur(4px)' }}>
      <div style={{
        background: '#fff',
        padding: '40px',
        borderRadius: '24px 8px 24px 8px',
        boxShadow: '0 24px 80px rgba(0,0,0,0.2)',
        width: '90%',
        maxWidth: '520px',
        position: 'relative',
        animation: 'fadeInScale 0.3s ease-out forwards',
        fontFamily: 'var(--font-body)',
      }}>
        <button onClick={onClose} style={{ position: 'absolute', top: '18px', right: '18px', fontSize: '20px', color: '#aaa', background: 'none', border: 'none', cursor: 'pointer', lineHeight: 1 }}>
          <FaTimes />
        </button>
        <h2 style={{ textAlign: 'center', marginBottom: '8px', fontFamily: 'var(--font-display)', fontSize: '26px', fontWeight: 600, color: '#1a1a1a' }}>
          রিভিউ লিখুন
        </h2>
        <p style={{ textAlign: 'center', color: '#888', fontSize: '14px', marginBottom: '24px' }}>{productName}</p>

        <div style={{ width: '60px', height: '3px', background: 'linear-gradient(to right, var(--orange), var(--green))', borderRadius: '2px', margin: '0 auto 28px' }} />

        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginBottom: '24px', gap: '4px' }}>
            {[1, 2, 3, 4, 5].map((index) => renderStarInput(index))}
            <span style={{ marginLeft: '12px', fontSize: '14px', color: '#888' }}>({rating} স্টার)</span>
          </div>

          <div style={{ marginBottom: '20px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#444', fontSize: '15px' }}>আপনার নাম</label>
            <input
              type="text"
              value={customerName}
              onChange={(e) => setCustomerName(e.target.value)}
              placeholder="নাম লিখুন"
              required
              style={{ width: '100%', padding: '12px 16px', border: '2px solid #F5D19D', borderRadius: '12px', fontSize: '15px', outline: 'none', fontFamily: 'var(--font-body)', boxSizing: 'border-box', transition: 'border-color 0.2s' }}
              onFocus={e => e.target.style.borderColor = '#4F7E17'}
              onBlur={e => e.target.style.borderColor = '#F5D19D'}
            />
          </div>

          <div style={{ marginBottom: '24px' }}>
            <label style={{ display: 'block', marginBottom: '8px', fontWeight: 600, color: '#444', fontSize: '15px' }}>আপনার মন্তব্য</label>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows="5"
              placeholder="আপনার অভিজ্ঞতা শেয়ার করুন..."
              required
              style={{ width: '100%', padding: '12px 16px', border: '2px solid #F5D19D', borderRadius: '12px', fontSize: '15px', resize: 'vertical', minHeight: '110px', outline: 'none', fontFamily: 'var(--font-body)', boxSizing: 'border-box', transition: 'border-color 0.2s' }}
              onFocus={e => e.target.style.borderColor = '#4F7E17'}
              onBlur={e => e.target.style.borderColor = '#F5D19D'}
            />
          </div>

          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button type="submit" style={{ padding: '12px 28px', background: 'linear-gradient(135deg, #4F7E17 0%, #3a5e11 100%)', color: '#fff', border: 'none', borderRadius: '12px 4px 12px 4px', fontSize: '15px', fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-body)', boxShadow: '0 4px 14px rgba(79,126,23,0.3)', letterSpacing: '0.02em' }}>
              জমা দিন ✓
            </button>
            <button type="button" onClick={onClose} style={{ padding: '12px 28px', background: '#f5f5f5', color: '#555', border: 'none', borderRadius: '4px 12px 4px 12px', fontSize: '15px', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
              বাতিল
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ─── Image Zoom Component ────────────────────────────────────────────────────
const ZOOM_FACTOR = 2.5;
const LENS_SIZE = 120;

const ImageZoom = ({ src, alt }) => {
  const containerRef = useRef(null);
  const [isHovering, setIsHovering] = useState(false);
  const [lensStyle, setLensStyle] = useState({});
  const [zoomBgStyle, setZoomBgStyle] = useState({});
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleMouseMove = (e) => {
    if (isMobile) return;
    const container = containerRef.current;
    if (!container) return;
    const rect = container.getBoundingClientRect();
    const mouseX = e.clientX - rect.left;
    const mouseY = e.clientY - rect.top;
    const halfLens = LENS_SIZE / 2;
    let lensX = Math.max(0, Math.min(mouseX - halfLens, rect.width - LENS_SIZE));
    let lensY = Math.max(0, Math.min(mouseY - halfLens, rect.height - LENS_SIZE));
    const bgX = -((lensX / rect.width) * rect.width * ZOOM_FACTOR);
    const bgY = -((lensY / rect.height) * rect.height * ZOOM_FACTOR);
    setLensStyle({ left: lensX, top: lensY, width: LENS_SIZE, height: LENS_SIZE });
    setZoomBgStyle({
      backgroundImage: `url(${src})`,
      backgroundSize: `${rect.width * ZOOM_FACTOR}px ${rect.height * ZOOM_FACTOR}px`,
      backgroundPosition: `${bgX}px ${bgY}px`,
      backgroundRepeat: 'no-repeat',
    });
  };

  if (isMobile) {
    return (
      <div style={{ width: '100%', aspectRatio: '4/3', overflow: 'hidden', display: 'block', marginBottom: '8px', background: '#FFF8EC' }}>
        <img src={src} alt={alt} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', transform: 'rotate(90deg)', transformOrigin: 'center center' }} draggable={false} />
      </div>
    );
  }

  return (
    <div style={{ position: 'relative', userSelect: 'none' }}>
      <div
        ref={containerRef}
        className="organic-card"
        style={{
          position: 'relative',
          height: '520px',
          background: 'linear-gradient(145deg, #FFF8EC 0%, #fff 60%, #FFF8EC 100%)',
          boxShadow: '0 8px 32px rgba(79,126,23,0.12), 0 2px 8px rgba(245,209,157,0.3)',
          cursor: 'crosshair',
          marginBottom: '16px',
          overflow: 'hidden',
        }}
        onMouseEnter={() => setIsHovering(true)}
        onMouseLeave={() => setIsHovering(false)}
        onMouseMove={handleMouseMove}
      >
        <img src={src} alt={alt} style={{ width: '100%', height: '100%', objectFit: 'cover', transform: 'rotate(90deg)', transformOrigin: 'center center', display: 'block' }} draggable={false} />

        {isHovering && (
          <div style={{ position: 'absolute', ...lensStyle, border: '2px solid #F88702', background: 'rgba(248,135,2,0.07)', boxShadow: '0 0 0 1px rgba(248,135,2,0.2)', zIndex: 10, pointerEvents: 'none' }} />
        )}

        {!isHovering && (
          <div style={{ position: 'absolute', bottom: '14px', right: '14px', background: 'rgba(79,126,23,0.88)', color: '#fff', fontSize: '12px', padding: '5px 14px', borderRadius: '20px', fontWeight: 600, fontFamily: 'var(--font-body)', letterSpacing: '0.03em', backdropFilter: 'blur(4px)' }}>
            🔍 Hover to zoom
          </div>
        )}
      </div>

      {isHovering && (
        <div style={{
          position: 'absolute',
          top: 0,
          left: 'calc(100% + 18px)',
          width: '440px',
          height: '520px',
          borderRadius: '20px 6px 20px 6px',
          boxShadow: '0 12px 48px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          zIndex: 50,
          border: '2px solid #F5D19D',
          background: '#FFF8EC',
          ...zoomBgStyle,
        }}>
          <div style={{ position: 'absolute', top: '10px', right: '10px', background: 'rgba(79,126,23,0.9)', color: '#fff', fontSize: '11px', padding: '3px 10px', borderRadius: '12px', fontWeight: 700, fontFamily: 'var(--font-body)' }}>
            {ZOOM_FACTOR}× zoom
          </div>
        </div>
      )}
    </div>
  );
};
// ──────────────────────────────────────────────────────────────────────────────

function ProductDetails({ addToCart, cartItem, toggleFavorite, isFavorite, getAuthHeaders }) {
  const navigate = useNavigate();
  const { id } = useParams();
  const [eventId] = useState(uuidv4());

  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [activeImage, setActiveImage] = useState('');
  const [flyingCart, setFlyingCart] = useState(null);
  const addCartButtonRef = useRef(null);

  const [activeTab, setActiveTab] = useState('specification');
  const [selectedColor, setSelectedColor] = useState('');
  const [selectedSize, setSelectedSize] = useState('');

  const [remainingTime, setRemainingTime] = useState(0);
  const [userSessionId, setUserSessionId] = useState(null);
  const [timerActive, setTimerActive] = useState(false);

  const [showReviewModal, setShowReviewModal] = useState(false);
  const [currentUserName, setCurrentUserName] = useState('');
  const [showSizeChart, setShowSizeChart] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [relatedProducts, setRelatedProducts] = useState([]);
  const [relatedScrollPosition, setRelatedScrollPosition] = useState(0);
  const relatedContainerRef = useRef(null);

  const [pdpSettings, setPdpSettings] = useState({
    callButton: { enabled: true, text: '📞 Call', phoneNumber: '+8809647001250' },
    whatsappButton: { enabled: true, text: 'WhatsApp Us', phoneNumber: '+8801345688562' },
    messengerButton: { enabled: true, text: 'Chat on Messenger', messengerLink: 'https://m.me/elevateemart' },
    secureTransaction: { enabled: true, text: '🔒 Secure transaction' },
    cashOnDelivery: { enabled: true, text: '✓ Cash on Delivery available' },
  });

  const TIMER_RESET_DAYS = 5;
  const memoizedGetAuthHeaders = useCallback(getAuthHeaders, [getAuthHeaders]);
  const API_BASE_URL = 'https://bazar-wala-main-server-974485829900.asia-south1.run.app';

  const checkLoginStatus = useCallback(() => {
    const token = localStorage.getItem('authToken');
    setIsLoggedIn(!!token);
  }, []);

  const fetchUserProfile = useCallback(async () => {
    const token = localStorage.getItem('authToken');
    if (!token) { setCurrentUserName(''); return; }
    try {
      const response = await fetch(`${API_BASE_URL}/api/user-profile`, { headers: { 'Authorization': `Bearer ${token}` } });
      if (response.ok) {
        const userData = await response.json();
        setCurrentUserName(userData.name || 'Anonymous User');
      } else {
        setCurrentUserName('Anonymous User');
      }
    } catch (error) {
      setCurrentUserName('Anonymous User');
    }
  }, []);

  const handleShare = async () => {
    const titleSlug = slugify(product.name, { lower: true, strict: true, trim: true });
    const shareUrl = `https://www.amanlifestylebd.com/product/${id}/${titleSlug}`;
    if (navigator.share) {
      try { await navigator.share({ title: product.name, text: `Check out ${product.name}`, url: shareUrl }); }
      catch (error) { console.log('Error sharing:', error); }
    } else {
      navigator.clipboard.writeText(shareUrl);
      alert('Link copied to clipboard!');
    }
  };

  const scrollRelatedProducts = (direction) => {
    if (relatedContainerRef.current) {
      const scrollAmount = 300;
      const newPosition = direction === 'left' ? relatedScrollPosition - scrollAmount : relatedScrollPosition + scrollAmount;
      relatedContainerRef.current.scrollTo({ left: newPosition, behavior: 'smooth' });
      setRelatedScrollPosition(newPosition);
    }
  };

  useEffect(() => {
    const fetchPDPSettings = async () => {
      try {
        const response = await fetch(`${API_BASE_URL}/api/product-details-settings`);
        if (response.ok) {
          const data = await response.json();
          if (data.success && data.settings) setPdpSettings(data.settings);
        }
      } catch (error) { console.error('Error fetching PDP settings:', error); }
    };
    fetchPDPSettings();
  }, []);

  useEffect(() => {
    const fetchProductData = async () => {
      setLoading(true); setError(null);
      try {
        const url = `https://bazar-wala-main-server-974485829900.asia-south1.run.app/products/${id}`;
        const response = await fetch(url, { headers: memoizedGetAuthHeaders() });
        if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
        const data = await response.json();
        setProduct(data);
        setActiveImage(data.galleryImages?.[0] || data.image);
        setSelectedColor(data.colors?.[0]?.hex || '');
        setSelectedSize(data.sizes?.[0] || '');
      } catch (err) {
        setError("পণ্যটি লোড করা যায়নি। অনুগ্রহ করে পরে আবার চেষ্টা করুন।");
        setProduct(null);
      } finally { setLoading(false); }
    };
    if (id) { fetchProductData(); checkLoginStatus(); fetchUserProfile(); }
  }, [id, memoizedGetAuthHeaders, checkLoginStatus, fetchUserProfile]);

  useEffect(() => {
    const fetchRelatedProducts = async () => {
      if (!product) return;
      try {
        const response = await fetch(`https://bazar-wala-main-server-974485829900.asia-south1.run.app/products?category=${product.category}&limit=10`, { headers: memoizedGetAuthHeaders() });
        if (response.ok) {
          const data = await response.json();
          const filteredProducts = data.products.filter(p => p.productId !== product.productId);
          setRelatedProducts(filteredProducts.slice(0, 10));
        }
      } catch (error) { console.error('Error fetching related products:', error); }
    };
    fetchRelatedProducts();
  }, [product, memoizedGetAuthHeaders]);

  const hasFiredRef = useRef(false);

  useEffect(() => {
    if (hasFiredRef.current) return;
    function getFbClickId() { const match = document.cookie.match(/_fbc=([^;]*)/); return match ? match[1] : null; }
    function getFbBrowserId() { const match = document.cookie.match(/_fbp=([^;]*)/); return match ? match[1] : null; }
    const externalId = localStorage.getItem('client_id');
    const fbc = getFbClickId(); const fbp = getFbBrowserId();
    const userData = { fbc, fbp, external_id: externalId };
    if (product && id) {
      const eventTime = Math.floor(Date.now() / 1000);
      ReactPixel.track('ViewContent', { content_name: product.name, content_category: product.category, content_ids: [product.productId], content_type: 'product', value: product.price, currency: 'BDT', event_id: eventId }, userData);
      ReactGA.event('view_item', { currency: 'BDT', value: product.price, items: [{ item_id: product.productId, item_name: product.name, item_category: product.category, price: product.price, quantity: 1 }], event_id: eventId });
      fetch('https://bazar-wala-main-server-974485829900.asia-south1.run.app/api/track-viewcontent', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ event_name: 'ViewContent', productId: product.productId, name: product.name, category: product.category, price: product.price, event_id: eventId, event_time: eventTime, fbc, fbp, external_id: externalId }) }).then(res => res.json()).then(data => console.log('ViewContent sent to Flask:', data)).catch(err => console.error('Error sending to Flask:', err));
      hasFiredRef.current = true;
    }
  }, [product, eventId, id]);

  useEffect(() => {
    let currentUserId = localStorage.getItem('user_session_id');
    if (!currentUserId) { currentUserId = uuidv4(); localStorage.setItem('user_session_id', currentUserId); }
    setUserSessionId(currentUserId);
    let interval;
    if (product && product.timer && product.time != null) {
      setTimerActive(true);
      const productTimerKey = `product_timer_${product.productId}_${currentUserId}`;
      const lastExpiredKey = `product_last_expired_${product.productId}_${currentUserId}`;
      let storedEndTime = localStorage.getItem(productTimerKey);
      let newEndTime;
      const now = new Date().getTime();
      if (storedEndTime) {
        newEndTime = parseInt(storedEndTime, 10);
        const lastExpiredTime = localStorage.getItem(lastExpiredKey);
        if (now >= newEndTime) {
          if (lastExpiredTime) {
            const timeSinceLastExpired = now - parseInt(lastExpiredTime, 10);
            if (timeSinceLastExpired >= TIMER_RESET_DAYS * 24 * 60 * 60 * 1000) {
              newEndTime = now + product.time * 60 * 1000;
              localStorage.setItem(productTimerKey, newEndTime.toString());
              localStorage.removeItem(lastExpiredKey);
            } else { setTimerActive(false); setRemainingTime(0); return; }
          } else { localStorage.setItem(lastExpiredKey, now.toString()); setTimerActive(false); setRemainingTime(0); return; }
        }
      } else {
        newEndTime = now + product.time * 60 * 1000;
        localStorage.setItem(productTimerKey, newEndTime.toString());
        localStorage.removeItem(lastExpiredKey);
      }
      interval = setInterval(() => {
        const currentTime = new Date().getTime();
        const timeLeft = newEndTime - currentTime;
        if (timeLeft <= 0) { clearInterval(interval); setRemainingTime(0); setTimerActive(false); if (!localStorage.getItem(lastExpiredKey)) localStorage.setItem(lastExpiredKey, currentTime.toString()); }
        else setRemainingTime(timeLeft);
      }, 1000);
    } else { setTimerActive(false); setRemainingTime(0); }
    return () => { if (interval) clearInterval(interval); };
  }, [product, TIMER_RESET_DAYS]);

  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => { if (flyingCart) { const timer = setTimeout(() => setFlyingCart(null), 3000); return () => clearTimeout(timer); } }, [flyingCart]);

  if (loading) {
    return (
      <div className="pdp-root" style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <FontInjector />
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>🌿</div>
          <p style={{ color: '#4F7E17', fontFamily: 'var(--font-body)', fontSize: '18px', fontWeight: 500 }}>Loading product...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="pdp-root" style={{ maxWidth: '1280px', margin: '0 auto', padding: '32px 16px' }}>
        <FontInjector />
        <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', color: '#4F7E17', fontFamily: 'var(--font-body)', marginBottom: '16px', textDecoration: 'none', fontSize: '16px', fontWeight: 500 }}>
          <FaArrowLeft style={{ marginRight: '8px' }} /> Back to Products
        </Link>
        <p style={{ color: '#CD0302', fontFamily: 'var(--font-body)' }}>{error}</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="pdp-root" style={{ maxWidth: '1280px', margin: '0 auto', padding: '32px 16px' }}>
        <FontInjector />
        <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', color: '#4F7E17', fontFamily: 'var(--font-body)', marginBottom: '16px', textDecoration: 'none', fontSize: '16px', fontWeight: 500 }}>
          <FaArrowLeft style={{ marginRight: '8px' }} /> Back to Products
        </Link>
        <p style={{ fontFamily: 'var(--font-body)' }}>Product not found!</p>
      </div>
    );
  }

  const handleAddToCart = () => {
    addToCart(product);
    function getFbClickId() { const match = document.cookie.match(/_fbc=([^;]*)/); return match ? match[1] : null; }
    function getFbBrowserId() { const match = document.cookie.match(/_fbp=([^;]*)/); return match ? match[1] : null; }
    const externalId = localStorage.getItem('client_id');
    const fbc = getFbClickId(); const fbp = getFbBrowserId();
    const userData = { fbc, fbp, external_id: externalId };
    const eventTime = Math.floor(Date.now() / 1000);
    ReactPixel.track('AddToCart', { content_name: product.name, content_category: product.category, content_ids: [product.productId], content_type: 'product', value: product.price, event_id: eventId, currency: 'BDT' }, userData);
    ReactGA.event('add_to_cart', { currency: 'BDT', value: product.price, items: [{ item_id: product.productId, item_name: product.name, item_category: product.category, price: product.price, quantity: 1 }], event_id: eventId });
    if (addCartButtonRef.current) {
      const rect = addCartButtonRef.current.getBoundingClientRect();
      setFlyingCart({ id: Date.now(), startX: rect.left + rect.width / 2, startY: rect.top + rect.height / 2 });
    }
    fetch('https://bazar-wala-main-server-974485829900.asia-south1.run.app/api/track-addtocart', { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ event_name: 'AddToCart', productId: product.productId, name: product.name, price: product.price, color: selectedColor, size: selectedSize, event_id: eventId, event_time: eventTime, fbc, fbp, external_id: externalId }) }).then(res => res.json()).then(data => console.log('AddToCart sent to Flask:', data)).catch(err => console.error('Error sending AddToCart to Flask:', err));
  };

  const hasStock = product.stock > 0;
  const productReviews = product.reviews || [];
  const averageRating = productReviews.length > 0 ? (productReviews.reduce((acc, r) => acc + r.rating, 0) / productReviews.length) : 0;

  const renderStars = (rating) => {
    const stars = [];
    for (let i = 1; i <= 5; i++) {
      if (rating >= i) stars.push(<FaStar key={i} style={{ color: '#F0C400' }} />);
      else if (rating >= i - 0.5) stars.push(<FaStarHalfAlt key={i} style={{ color: '#F0C400' }} />);
      else stars.push(<FaRegStar key={i} style={{ color: '#ddd' }} />);
    }
    return stars;
  };

  const handleWhatsAppClick = () => {
    const titleSlug = slugify(product.name, { lower: true, strict: true, trim: true });
    const link = `https://www.amanlifestylebd.com/product/${id}/${titleSlug}`;
    const message = `আমি জানতে চাই আপনাদের ${product.name} প্রোডাক্ট সম্পর্কে ${link}`;
    window.open(`https://wa.me/${pdpSettings.whatsappButton.phoneNumber}?text=${encodeURIComponent(message)}`, '_blank');
  };

  const handleMessengerClick = () => { window.open(pdpSettings.messengerButton.messengerLink, '_blank'); };
  const handleClick = () => { window.location.href = `tel:${pdpSettings.callButton.phoneNumber}`; };

  const handleProceedToCheckout = () => {
    const productToAddForCheckout = { ...product, quantity: 1 };
    let cartItemsForNavigation = [];
    const existingIndex = cartItem.findIndex(item => item.productId === productToAddForCheckout.productId);
    if (existingIndex !== -1) {
      cartItemsForNavigation = cartItem.map((item, index) => index === existingIndex ? { ...item, quantity: item.quantity + 1 } : item);
    } else {
      cartItemsForNavigation = [...cartItem, productToAddForCheckout];
    }
    const totalForNavigation = cartItemsForNavigation.reduce((acc, item) => {
      const price = typeof item.price === 'number' ? item.price : parseFloat(item.price) || 0;
      const quantity = typeof item.quantity === 'number' ? item.quantity : parseInt(item.quantity) || 0;
      return acc + (price * quantity);
    }, 0);
    addToCart(product);
    navigate('/checkout', { state: { cartItems: cartItemsForNavigation, total: totalForNavigation } });
  };

  const formatTime = (milliseconds) => {
    const totalSeconds = Math.floor(milliseconds / 1000);
    const days = Math.floor(totalSeconds / (3600 * 24));
    const hours = Math.floor((totalSeconds % (3600 * 24)) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return { days, hours, minutes, seconds };
  };

  const formattedTime = formatTime(remainingTime);

  const schemaData = {
    "@context": "https://schema.org/",
    "@type": "Product",
    "name": product.name,
    "image": product.image,
    "description": product.description,
    "sku": product.sku || product.id,
    "offers": {
      "@type": "Offer",
      "priceCurrency": "BDT",
      "price": product.price,
      "availability": product.stock ? "https://schema.org/InStock" : "https://schema.org/OutOfStock"
    }
  };

  const handleAddReviewClick = () => {
    if (!isLoggedIn) { navigate('/login'); return; }
    setShowReviewModal(true);
  };

  const handleReviewSubmit = async (rating, comment, submittedUserName) => {
    try {
      const token = localStorage.getItem('authToken');
      if (!token) { setShowReviewModal(false); navigate('/login'); return; }
      const response = await fetch(`${API_BASE_URL}/api/add-product-review`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ productId: product.productId, rating, comment, customerName: submittedUserName }),
      });
      if (!response.ok) { const errorData = await response.json(); throw new Error(errorData.error || 'রিভিউ যোগ করতে সমস্যা হয়েছে।'); }
      setShowReviewModal(false);
      const updatedProductResponse = await fetch(`https://bazar-wala-main-server-974485829900.asia-south1.run.app/products/${id}`, { headers: memoizedGetAuthHeaders() });
      if (updatedProductResponse.ok) { const updatedProductData = await updatedProductResponse.json(); setProduct(updatedProductData); }
    } catch (err) { console.error("রিভিউ সাবমিট করতে সমস্যা:", err); }
  };

  return (
    <>
      <FontInjector />
      <Helmet>
        <title>{product.name} | Bazarwala</title>
        <meta name="description" content={product.description || "Bazarwala বাংলাদেশের একমাত্র সহজলভ্য ও দ্রুত ডেলিভারি দেওয়া ই-কমার্স প্রতিষ্ঠান"} />
        <meta name="keywords" content={`ElevateMart, সহজলভ্য, দ্রুত ডেলিভারি, ই-কমার্স প্রতিষ্ঠান, ${product.name}, ${product.category}`} />
        <meta property="og:image" content={product.image} />
        <meta property="og:type" content="product" />
        <meta property="og:title" content={product.name} />
        <meta property="og:description" content={product.description} />
        <meta property="og:url" content={`https://www.amanlifestylebd.com/product/${id}/${slugify(product.name, { lower: true, strict: true, trim: true })}`} />
        <script type="application/ld+json">{JSON.stringify(schemaData)}</script>
      </Helmet>

      {/* Root wrapper */}
      <div className="pdp-root leaf-pattern">

        {/* ── Breadcrumb ───────────────────────────────────────────── */}
        <div className="breadcrumb-bar">
          <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '0 20px' }}>
            <Link to="/" style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', color: '#4F7E17', textDecoration: 'none', fontSize: '15px', fontWeight: 600, fontFamily: 'var(--font-body)' }}>
              <FaArrowLeft style={{ fontSize: '13px' }} />
              Back to results
            </Link>
          </div>
        </div>

        {/* ── Main Product Section ─────────────────────────────────── */}
        <style>{`
          .pdp-main-grid {
            display: flex;
            flex-direction: column;
            gap: 0;
          }
          @media(min-width:1024px){
            .pdp-main-grid {
              display: grid;
              grid-template-columns: 5fr 4fr 3fr;
              gap: 0 20px;
              align-items: start;
            }
            .pdp-col-image { margin-bottom: 0 !important; }
            .pdp-col-info { margin-bottom: 0 !important; }
            .pdp-col-buy { margin-bottom: 0 !important; }
          }
          .pdp-col-info .info-card {
            border-radius: 8px 24px 8px 24px;
          }
          @media(max-width:1023px){
            .pdp-col-info .info-card {
              border-radius: 0 !important;
              border-left: none !important;
              border-right: none !important;
            }
            .pdp-col-buy .buy-box {
              border-radius: 0 !important;
              border-left: none !important;
              border-right: none !important;
              border-top: none !important;
              padding: 16px 14px !important;
              position: static !important;
            }
            .pdp-col-image {
              padding: 0 !important;
            }
            .desktop-price-block, .desktop-stock-block { display: none !important; }
          }
          @media(min-width:1024px){
            .pdp-main-grid { padding: 0 16px; }
          }
        `}</style>
        <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '16px 0 0', boxSizing: 'border-box' }}>
          <div className="pdp-main-grid">

            {/* ──── LEFT: Images ──────────────────────────────────── */}
            <div className="pdp-col-image" style={{ marginBottom: '0' }}>

              <div style={{ position: 'sticky', top: '16px', overflow: 'visible' }}>
                {/* Image + Badges */}
                <div style={{ position: 'relative', overflow: 'visible' }}>
                  {product.discount && !product.isPopular && (
                    <div className="badge-red" style={{ position: 'absolute', top: '14px', left: '14px', zIndex: 10 }}>
                      {product.discount}
                    </div>
                  )}
                  {product.isPopular && (
                    <div className="badge-orange" style={{ position: 'absolute', top: '14px', left: '14px', zIndex: 10 }}>
                      🔥 Best Seller
                    </div>
                  )}
                  <button
                    onClick={() => toggleFavorite(product.productId)}
                    style={{ position: 'absolute', top: '14px', right: '14px', zIndex: 20, background: 'rgba(255,255,255,0.85)', border: 'none', cursor: 'pointer', width: '40px', height: '40px', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 10px rgba(0,0,0,0.12)', fontSize: '20px', backdropFilter: 'blur(6px)' }}
                  >
                    {isFavorite(product.productId) ? <FilledHeart style={{ color: '#CD0302' }} /> : <EmptyHeart style={{ color: '#ccc' }} />}
                  </button>
                  <ImageZoom src={activeImage} alt={product.name} />
                </div>

                {/* Thumbnails */}
                <div style={{ display: 'flex', gap: '10px', overflowX: 'auto', padding: '4px 2px 8px', justifyContent: 'center' }}
                  className="scrollbar-hide">
                  {(product.galleryImages && product.galleryImages.length > 0 ? product.galleryImages : [product.image]).map((img, index) => (
                    <button
                      key={index}
                      onClick={() => setActiveImage(img)}
                      className={`thumb-btn ${activeImage === img ? 'active' : ''}`}
                      style={{ padding: 0, cursor: 'pointer' }}
                    >
                      <img src={img} alt={`${product.name} - ${index + 1}`} style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* ──── MIDDLE: Product Info ──────────────────────────── */}
            <div className="pdp-col-info">
            <div className="info-card" style={{ background: '#fff', border: '2px solid #F5D19D' }}>

              {/* Header area */}
              <div style={{ padding: 'clamp(14px, 3vw, 28px) clamp(14px, 3vw, 28px) clamp(12px, 2vw, 20px)', borderBottom: '1.5px solid #F5D19D' }}>
                <h1 className="product-title" style={{ marginBottom: '16px' }}>{product.name}</h1>

                {/* Rating row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '3px', fontSize: '18px' }}>
                    {renderStars(averageRating)}
                  </div>
                  {productReviews.length > 0 ? (
                    <span style={{ color: '#4F7E17', fontSize: '15px', fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>
                      {averageRating.toFixed(1)} ({productReviews.length} reviews)
                    </span>
                  ) : (
                    <span style={{ color: '#aaa', fontSize: '14px', fontFamily: 'var(--font-body)' }}>No reviews yet</span>
                  )}
                </div>

                {/* Stock + buyers row */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <span className={hasStock ? 'stock-in' : 'stock-out'}>
                    {hasStock ? `✓ In Stock (${product.stock})` : '✗ Out of Stock'}
                  </span>
                  {product.buyers > 0 && (
                    <span className="buyers-pill">🛒 {product.buyers} bought</span>
                  )}
                </div>
              </div>

              {/* Price + Timer */}
              <div style={{ padding: 'clamp(12px, 3vw, 20px) clamp(14px, 3vw, 28px)', borderBottom: '1.5px solid #F5D19D' }}>
                {/* Timer */}
                {timerActive && remainingTime > 0 && (
                  <div className="timer-box" style={{ marginBottom: '20px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px', flexWrap: 'wrap' }}>
                      <span style={{ color: '#CD0302', fontWeight: 700, fontSize: '15px', fontFamily: 'var(--font-body)' }}>⏰ Deal ends in:</span>
                      <div style={{ display: 'flex', gap: '6px', alignItems: 'flex-start' }}>
                        {formattedTime.days > 0 && (
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div className="timer-unit">{String(formattedTime.days).padStart(2, '0')}</div>
                            <div className="timer-label">days</div>
                          </div>
                        )}
                        {(formattedTime.hours > 0 || formattedTime.days > 0) && (
                          <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <div className="timer-unit">{String(formattedTime.hours).padStart(2, '0')}</div>
                            <div className="timer-label">hrs</div>
                          </div>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <div className="timer-unit">{String(formattedTime.minutes).padStart(2, '0')}</div>
                          <div className="timer-label">min</div>
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                          <div className="timer-unit">{String(formattedTime.seconds).padStart(2, '0')}</div>
                          <div className="timer-label">sec</div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                <div style={{ display: 'flex', alignItems: 'baseline', gap: '14px', flexWrap: 'wrap' }}>
                  <span className="price-tag">৳{product.price}</span>
                  {product.originalPrice && product.originalPrice > product.price && (
                    <>
                      <span className="price-original">৳{product.originalPrice}</span>
                      <span className="price-discount-badge">
                        -{Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)}% OFF
                      </span>
                    </>
                  )}
                </div>
              </div>

              {/* Colors */}
              {product.isColorAvailable && product.colors && product.colors.length > 0 && (
                <div style={{ padding: 'clamp(12px, 3vw, 20px) clamp(14px, 3vw, 28px)', borderBottom: '1.5px solid #F5D19D' }}>
                  <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#333', marginBottom: '12px', fontFamily: 'var(--font-body)' }}>
                    Color: <span style={{ color: '#F88702' }}>{product.colors.find(c => c.hex === selectedColor)?.name || ''}</span>
                  </h3>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                    {product.colors.map((color, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedColor(color.hex)}
                        className={`color-swatch ${selectedColor === color.hex ? 'active' : ''}`}
                      >
                        <div style={{ width: '22px', height: '22px', borderRadius: '50%', backgroundColor: color.hex, border: '2px solid rgba(0,0,0,0.1)', flexShrink: 0 }} />
                        <span>{color.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Sizes */}
              {product.isSizeAvailable && product.sizes && product.sizes.length > 0 && (
                <div style={{ padding: 'clamp(12px, 3vw, 20px) clamp(14px, 3vw, 28px)', borderBottom: '1.5px solid #F5D19D' }}>
                  <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#333', marginBottom: '12px', fontFamily: 'var(--font-body)' }}>
                    Size: <span style={{ color: '#F88702' }}>{selectedSize}</span>
                  </h3>
                  <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap', marginBottom: '14px' }}>
                    {product.sizes.map((size, index) => (
                      <button key={index} onClick={() => setSelectedSize(size)} className={`size-btn ${selectedSize === size ? 'active' : ''}`}>
                        {size}
                      </button>
                    ))}
                  </div>

                  {product.sizeChart && product.sizeChart.length > 0 && product.sizeChartColumns && product.sizeChartColumns.length > 0 && (
                    <div>
                      <button
                        onClick={() => setShowSizeChart(prev => !prev)}
                        style={{ fontSize: '14px', color: '#4F7E17', textDecoration: 'underline', background: 'none', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', fontFamily: 'var(--font-body)', fontWeight: 600, padding: 0 }}
                      >
                        📏 {showSizeChart ? 'Hide' : 'View'} Size Chart
                      </button>

                      {showSizeChart && (
                        <div style={{ marginTop: '14px', border: '1.5px solid #F5D19D', borderRadius: '12px', overflow: 'hidden' }}>
                          {/* INCH */}
                          <div style={{ background: '#F0C400', padding: '6px 14px' }}>
                            <span style={{ color: '#fff', fontSize: '12px', fontWeight: 700, fontFamily: 'var(--font-body)', letterSpacing: '0.05em' }}>INCH</span>
                          </div>
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', fontFamily: 'var(--font-body)' }}>
                              <thead style={{ background: '#FFF8EC' }}>
                                <tr>
                                  <th style={{ border: '1px solid #F5D19D', padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#555' }}>Size</th>
                                  {product.sizeChartColumns.map(col => <th key={col} style={{ border: '1px solid #F5D19D', padding: '10px 14px', textAlign: 'center', fontWeight: 600, color: '#555' }}>{col}</th>)}
                                </tr>
                              </thead>
                              <tbody>
                                {product.sizeChart.map((row, idx) => (
                                  <tr key={idx} style={{ background: row.size === selectedSize ? '#FFF8EC' : idx % 2 === 0 ? '#fff' : '#fafafa', fontWeight: row.size === selectedSize ? 700 : 400 }}>
                                    <td style={{ border: '1px solid #F5D19D', padding: '10px 14px', color: '#222' }}>{row.size === selectedSize ? `▶ ${row.size}` : row.size}</td>
                                    {product.sizeChartColumns.map(col => <td key={col} style={{ border: '1px solid #F5D19D', padding: '10px 14px', textAlign: 'center', color: '#555' }}>{row[`${col}_inch`] || '—'}</td>)}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>

                          {/* CM */}
                          <div style={{ background: '#4F7E17', padding: '6px 14px', marginTop: '8px' }}>
                            <span style={{ color: '#fff', fontSize: '12px', fontWeight: 700, fontFamily: 'var(--font-body)', letterSpacing: '0.05em' }}>CM</span>
                          </div>
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', fontFamily: 'var(--font-body)' }}>
                              <thead style={{ background: '#edf5e0' }}>
                                <tr>
                                  <th style={{ border: '1px solid #F5D19D', padding: '10px 14px', textAlign: 'left', fontWeight: 600, color: '#555' }}>Size</th>
                                  {product.sizeChartColumns.map(col => <th key={col} style={{ border: '1px solid #F5D19D', padding: '10px 14px', textAlign: 'center', fontWeight: 600, color: '#555' }}>{col}</th>)}
                                </tr>
                              </thead>
                              <tbody>
                                {product.sizeChart.map((row, idx) => (
                                  <tr key={idx} style={{ background: row.size === selectedSize ? '#FFF8EC' : idx % 2 === 0 ? '#fff' : '#fafafa', fontWeight: row.size === selectedSize ? 700 : 400 }}>
                                    <td style={{ border: '1px solid #F5D19D', padding: '10px 14px', color: '#222' }}>{row.size === selectedSize ? `▶ ${row.size}` : row.size}</td>
                                    {product.sizeChartColumns.map(col => <td key={col} style={{ border: '1px solid #F5D19D', padding: '10px 14px', textAlign: 'center', color: '#555' }}>{row[`${col}_cm`] || '—'}</td>)}
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          <p style={{ fontSize: '12px', color: '#aaa', padding: '8px 14px', background: '#FFF8EC', fontFamily: 'var(--font-body)' }}>* Expected deviation &lt; 3%</p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Description preview */}
              {product.description && (
                <div style={{ padding: 'clamp(12px, 3vw, 20px) clamp(14px, 3vw, 28px)', borderBottom: '1.5px solid #F5D19D' }}>
                  <div className="desc-preview">
                    <h3 style={{ fontSize: '15px', fontWeight: 700, color: '#333', padding: '16px 18px 10px', margin: 0, fontFamily: 'var(--font-body)' }}>
                      🌿 About this item
                    </h3>
                    <div style={{ position: 'relative' }}>
                      <div style={{ fontSize: '14px', color: '#555', lineHeight: '1.7', padding: '0 18px', maxHeight: '90px', overflow: 'hidden', fontFamily: 'var(--font-body)' }}>
                        {product.description.split('\n').slice(0, 6).map((line, index) => {
                          if (!line.trim()) return null;
                          const cleanLine = line.trim().startsWith('**') ? line.trim().substring(2) : line.trim();
                          return (
                            <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '6px' }}>
                              <span style={{ color: '#4F7E17', flexShrink: 0 }}>•</span>
                              <span>{cleanLine}</span>
                            </div>
                          );
                        })}
                      </div>
                      <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '60px', background: 'linear-gradient(to bottom, transparent, rgba(255,248,236,0.9) 50%, #FFF8EC)', display: 'flex', alignItems: 'flex-end', justifyContent: 'center', paddingBottom: '10px' }}>
                        <button
                          onClick={() => { setActiveTab('specification'); setTimeout(() => document.getElementById('product-tabs')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50); }}
                          style={{ fontSize: '13px', fontWeight: 700, color: '#F88702', border: '1.5px solid #F88702', background: '#fff', padding: '5px 16px', borderRadius: '20px', cursor: 'pointer', fontFamily: 'var(--font-body)' }}
                        >
                          See more ▾
                        </button>
                      </div>
                    </div>
                    <div style={{ height: '52px' }} />
                  </div>
                </div>
              )}

              {/* Share */}
              <div style={{ padding: 'clamp(12px, 3vw, 20px) clamp(14px, 3vw, 28px)' }}>
                <button className="share-btn" onClick={handleShare}>
                  <FaShare />
                  Share this product
                </button>
              </div>
            </div>{/* close info-card */}
            </div>{/* close pdp-col-info */}

            {/* ──── RIGHT: Buy Box ────────────────────────────────── */}
            <div className="pdp-col-buy">
              <div className="buy-box">
                {/* Desktop price */}
                <div style={{ display: 'none' }} className="desktop-price-block">
                  <style>{`@media(min-width:1024px){.desktop-price-block{display:block!important;}}`}</style>
                  <div className="price-tag" style={{ marginBottom: '6px' }}>৳{product.price}</div>
                  {product.originalPrice && product.originalPrice > product.price && (
                    <div style={{ fontSize: '15px', color: '#888', marginBottom: '4px', fontFamily: 'var(--font-body)' }}>
                      List: <span style={{ textDecoration: 'line-through' }}>৳{product.originalPrice}</span>
                    </div>
                  )}
                </div>

                {/* Desktop stock */}
                <div style={{ display: 'none', marginBottom: '20px', paddingBottom: '20px', borderBottom: '1.5px solid #F5D19D' }} className="desktop-stock-block">
                  <style>{`@media(min-width:1024px){.desktop-stock-block{display:block!important;}}`}</style>
                  <span className={hasStock ? 'stock-in' : 'stock-out'} style={{ fontSize: '15px' }}>
                    {hasStock ? '✓ In Stock' : '✗ Out of Stock'}
                  </span>
                  {hasStock && <div style={{ color: '#888', fontSize: '13px', marginTop: '6px', fontFamily: 'var(--font-body)' }}>Ships from Bangladesh</div>}
                </div>

                {/* Action buttons */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                  <button
                    ref={addCartButtonRef}
                    onClick={handleProceedToCheckout}
                    disabled={!hasStock}
                    className="btn-buy-now"
                  >
                    {hasStock ? 'Buy Now' : 'Out of Stock'}
                  </button>

                  <button
                    onClick={handleAddToCart}
                    disabled={!hasStock}
                    className="btn-add-cart"
                  >
                    <FaShoppingCart style={{ display: 'inline', marginRight: '8px', verticalAlign: 'middle' }} />
                    {hasStock ? 'Add to Cart' : 'Out of Stock'}
                  </button>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px' }}>
                    {pdpSettings.callButton.enabled && (
                      <button onClick={handleClick} className="btn-call" style={{ fontSize: '13px' }}>
                        {pdpSettings.callButton.text}
                      </button>
                    )}
                    {pdpSettings.whatsappButton.enabled && (
                      <button onClick={handleWhatsAppClick} className="btn-whatsapp" style={{ fontSize: '13px' }}>
                        <FontAwesomeIcon icon={faWhatsapp} style={{ marginRight: '6px' }} />
                        {pdpSettings.whatsappButton.text}
                      </button>
                    )}
                    {pdpSettings.messengerButton.enabled && (
                      <button
                        onClick={handleMessengerClick}
                        className="btn-messenger"
                        style={{ fontSize: '13px', gridColumn: (pdpSettings.callButton.enabled && pdpSettings.whatsappButton.enabled) ? '1 / -1' : 'auto' }}
                      >
                        <FontAwesomeIcon icon={faFacebookMessenger} style={{ marginRight: '6px' }} />
                        {pdpSettings.messengerButton.text}
                      </button>
                    )}
                  </div>
                </div>

                {/* Security info */}
                {(pdpSettings.secureTransaction.enabled || pdpSettings.cashOnDelivery.enabled) && (
                  <div style={{ marginTop: '18px', paddingTop: '16px', borderTop: '1.5px solid #F5D19D', display: 'flex', flexDirection: 'column', gap: '6px' }}>
                    {pdpSettings.secureTransaction.enabled && (
                      <div style={{ fontSize: '13px', color: '#777', fontFamily: 'var(--font-body)' }}>{pdpSettings.secureTransaction.text}</div>
                    )}
                    {pdpSettings.cashOnDelivery.enabled && (
                      <div style={{ fontSize: '13px', color: '#4F7E17', fontWeight: 600, fontFamily: 'var(--font-body)' }}>{pdpSettings.cashOnDelivery.text}</div>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ── Tabs Section ───────────────────────────────────────── */}
          <div id="product-tabs" style={{ marginTop: '16px', background: '#fff', border: '2px solid #F5D19D', borderRadius: 'clamp(0px, 2vw, 20px) clamp(0px, 2vw, 8px) clamp(0px, 2vw, 20px) clamp(0px, 2vw, 8px)', padding: 'clamp(16px, 4vw, 32px)', boxSizing: 'border-box', margin: '16px 12px 0' }}>
            <div className="tab-bar">
              <button className={`tab-btn ${activeTab === 'specification' ? 'active' : ''}`} onClick={() => setActiveTab('specification')}>
                Specifications
              </button>
              <button className={`tab-btn ${activeTab === 'reviews' ? 'active' : ''}`} onClick={() => setActiveTab('reviews')}>
                Reviews ({productReviews.length})
              </button>
            </div>

            <div style={{ paddingTop: '28px' }}>
              {/* ─ Specification Tab ─ */}
              {activeTab === 'specification' && (
                <div style={{ maxWidth: '900px' }}>
                  {product.specifications && product.specifications.length > 0 && (
                    <div style={{ marginBottom: '36px' }}>
                      <h2 className="section-heading" style={{ marginBottom: '18px' }}>
                        <span className="organic-section-header">Details</span>
                      </h2>
                      <div style={{ border: '1.5px solid #F5D19D', borderRadius: '14px', overflow: 'hidden' }}>
                        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                          <tbody>
                            {product.specifications.map((spec, index) => (
                              <tr key={index} className={index % 2 === 0 ? 'spec-row-even' : 'spec-row-odd'}>
                                <td className="spec-key">{spec.key}</td>
                                <td className="spec-val">{spec.value}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {!product.specifications?.length && (
                    <p style={{ color: '#aaa', fontFamily: 'var(--font-body)', fontSize: '15px' }}>No specifications available for this product.</p>
                  )}

                  {product.description && (
                    <div style={{ marginBottom: '36px' }}>
                      <h2 className="section-heading" style={{ marginBottom: '18px' }}>
                        <span className="organic-section-header">Description</span>
                      </h2>
                      <div>
                        {product.description.split('\n').map((line, index) => {
                          if (!line.trim()) return null;
                          const isSpecialLine = line.trim().startsWith('**');
                          const cleanLine = isSpecialLine ? line.trim().substring(2) : line.trim();
                          return (
                            <div key={index} style={{ marginBottom: '12px', display: 'flex', gap: '12px' }}>
                              <span style={{ color: isSpecialLine ? '#F88702' : '#4F7E17', marginTop: '2px', fontSize: '16px', flexShrink: 0 }}>
                                {isSpecialLine ? '✨' : '•'}
                              </span>
                              <span style={{ fontSize: '15px', fontFamily: 'var(--font-body)', color: isSpecialLine ? '#1a1a1a' : '#555', fontWeight: isSpecialLine ? 700 : 400, lineHeight: '1.7' }}>
                                {cleanLine}
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {product.youtubeUrl && (
                    <div>
                      <h2 className="section-heading" style={{ marginBottom: '18px' }}>
                        <span className="organic-section-header">Video</span>
                      </h2>
                      <div style={{ position: 'relative', width: '100%', paddingBottom: '56.25%', borderRadius: '16px', overflow: 'hidden' }}>
                        <iframe src={product.youtubeUrl} title="Product Video" frameBorder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%' }} />
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ─ Reviews Tab ─ */}
              {activeTab === 'reviews' && (
                <div style={{ maxWidth: '900px' }}>
                  <h2 className="section-heading" style={{ marginBottom: '24px' }}>
                    <span className="organic-section-header">Customer Reviews</span>
                  </h2>

                  {productReviews.length > 0 ? (
                    <>
                      {/* Rating summary */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '24px', marginBottom: '28px', padding: '20px 24px', background: '#FFF8EC', border: '1.5px solid #F5D19D', borderRadius: '16px' }}>
                        <div style={{ textAlign: 'center' }}>
                          <div style={{ fontFamily: 'var(--font-display)', fontSize: '56px', fontWeight: 600, color: '#1a1a1a', lineHeight: 1 }}>{averageRating.toFixed(1)}</div>
                          <div style={{ display: 'flex', justifyContent: 'center', margin: '8px 0', gap: '3px', fontSize: '20px' }}>{renderStars(averageRating)}</div>
                          <div style={{ color: '#888', fontSize: '14px', fontFamily: 'var(--font-body)' }}>{productReviews.length} ratings</div>
                        </div>
                      </div>

                      {/* Review cards */}
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', marginBottom: '28px' }}>
                        {productReviews.map((review) => (
                          <div key={review.reviewId} className="review-card">
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                              <span style={{ fontWeight: 700, color: '#222', fontSize: '16px', fontFamily: 'var(--font-body)' }}>{review.userName}</span>
                              <div style={{ display: 'flex', gap: '3px', fontSize: '16px' }}>{renderStars(review.rating)}</div>
                            </div>
                            <p style={{ color: '#555', lineHeight: '1.7', fontSize: '15px', margin: 0, fontFamily: 'var(--font-body)' }}>{review.comment}</p>
                          </div>
                        ))}
                      </div>
                    </>
                  ) : (
                    <p style={{ color: '#aaa', fontFamily: 'var(--font-body)', fontSize: '16px', marginBottom: '28px' }}>No reviews yet. Be the first to review this product!</p>
                  )}

                  <div style={{ paddingTop: '24px', borderTop: '1.5px solid #F5D19D' }}>
                    <button
                      onClick={handleAddReviewClick}
                      style={{ padding: '14px 28px', background: 'linear-gradient(135deg, #4F7E17 0%, #3a5e11 100%)', color: '#fff', border: 'none', borderRadius: '12px 4px 12px 4px', fontSize: '16px', fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font-body)', boxShadow: '0 4px 16px rgba(79,126,23,0.3)', letterSpacing: '0.02em', display: 'inline-flex', alignItems: 'center', gap: '8px' }}
                      onMouseEnter={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #5d941c 0%, #4F7E17 100%)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                      onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, #4F7E17 0%, #3a5e11 100%)'; e.currentTarget.style.transform = 'translateY(0)'; }}
                    >
                      <FaComments />
                      Write a Review
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* ── Related Products ──────────────────────────────────────── */}
        {relatedProducts.length > 0 && (
          <div style={{ maxWidth: '1280px', margin: '0 auto', padding: '16px 12px 40px', boxSizing: 'border-box' }}>
            <div className="related-section">
              <h2 className="section-heading" style={{ marginBottom: '24px' }}>
                <span className="organic-section-header">Related items</span>
              </h2>

              {/* Mobile: 2-col grid */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }} className="mobile-related">
                <style>{`@media(min-width:768px){.mobile-related{display:none!important;}.desktop-related{display:block!important;}}`}</style>
                {relatedProducts.map((relatedProduct) => (
                  <div key={relatedProduct.productId}>
                    <ProductCard product={relatedProduct} addToCart={addToCart} toggleFavorite={toggleFavorite} isFavorite={isFavorite} cartItems={cartItem} navigate={navigate} />
                  </div>
                ))}
              </div>

              {/* Desktop: horizontal scroll */}
              <div style={{ display: 'none', position: 'relative' }} className="desktop-related">
                <button
                  onClick={() => scrollRelatedProducts('left')}
                  style={{ position: 'absolute', left: '-20px', top: '50%', transform: 'translateY(-50%)', zIndex: 10, width: '42px', height: '42px', background: '#fff', border: '1.5px solid #F5D19D', borderRadius: '50%', boxShadow: '0 3px 12px rgba(0,0,0,0.12)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#FFF8EC'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                >
                  <FaChevronLeft style={{ color: '#4F7E17', fontSize: '16px' }} />
                </button>

                <div ref={relatedContainerRef} className="scrollbar-hide" style={{ overflowX: 'auto', scrollBehavior: 'smooth' }}>
                  <div style={{ display: 'flex', gap: '16px', paddingBottom: '8px' }}>
                    {relatedProducts.map((relatedProduct) => (
                      <div key={relatedProduct.productId} style={{ flexShrink: 0, width: '290px' }}>
                        <ProductCard product={relatedProduct} addToCart={addToCart} toggleFavorite={toggleFavorite} isFavorite={isFavorite} cartItems={cartItem} navigate={navigate} />
                      </div>
                    ))}
                  </div>
                </div>

                <button
                  onClick={() => scrollRelatedProducts('right')}
                  style={{ position: 'absolute', right: '-20px', top: '50%', transform: 'translateY(-50%)', zIndex: 10, width: '42px', height: '42px', background: '#fff', border: '1.5px solid #F5D19D', borderRadius: '50%', boxShadow: '0 3px 12px rgba(0,0,0,0.12)', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', transition: 'all 0.2s' }}
                  onMouseEnter={e => e.currentTarget.style.background = '#FFF8EC'}
                  onMouseLeave={e => e.currentTarget.style.background = '#fff'}
                >
                  <FaChevronRight style={{ color: '#4F7E17', fontSize: '16px' }} />
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Flying cart animation */}
      {flyingCart && (
        <div
          key={flyingCart.id}
          style={{
            position: 'fixed',
            left: flyingCart.startX,
            top: flyingCart.startY,
            color: '#4F7E17',
            fontSize: '26px',
            pointerEvents: 'none',
            zIndex: 9999,
            animation: 'flyUpAndFade 3s forwards',
            fontFamily: 'var(--font-body)',
            fontWeight: 700,
          }}
        >
          <FaShoppingCart style={{ display: 'inline' }} /> +1
        </div>
      )}

      {/* Review Modal */}
      {showReviewModal && (
        <ReviewModal
          onClose={() => setShowReviewModal(false)}
          onSubmit={handleReviewSubmit}
          productName={product.name}
          userName={currentUserName}
          isLoggedIn={isLoggedIn}
        />
      )}
    </>
  );
}

export default ProductDetails;
